<template>
  <div class="wrapper">
    <!-- Page header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol sm="7">
            <h1 class="h4 mb-1 font-weight-bold">My receipts</h1>
            <p class="text-muted mb-0">Upload receipts here, then attach them to a claim</p>
          </CCol>
          <CCol sm="5" class="d-flex justify-content-sm-end mt-3 mt-sm-0">
            <CButton color="light" class="mr-2" aria-label="Refresh" title="Refresh" @click="refreshTable">
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add receipt
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard class="border-0">
      <!-- First load -->
      <CCardBody v-if="loading && !items.length" class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3 mb-0">Loading your receipts…</p>
      </CCardBody>

      <!-- Error -->
      <CCardBody v-else-if="loadError && !items.length" class="text-center py-5">
        <CIcon name="cil-warning" size="3xl" class="mb-3 text-danger" />
        <h2 class="h5">Couldn't load your receipts</h2>
        <p class="text-muted mb-3">Check your connection and try again.</p>
        <CButton color="primary" @click="refreshTable">
          <CIcon name="cil-reload" /> Try again
        </CButton>
      </CCardBody>

      <!-- Empty -->
      <CCardBody v-else-if="!items.length" class="text-center py-5">
        <CIcon name="cil-file" size="3xl" class="mb-3 text-muted" />
        <h2 class="h5">No receipts yet</h2>
        <p class="text-muted mb-3">Take a photo of a receipt or upload a PDF so you can claim it back.</p>
        <CButton color="primary" @click="addNew">
          <CIcon name="cil-plus" /> Add receipt
        </CButton>
      </CCardBody>

      <template v-else>
        <CProgress v-if="loading" :value="100" animated color="primary" size="xs" />
        <!-- Desktop table -->
        <CCardBody class="d-none d-lg-block">
          <CDataTable
            :items="rows"
            :fields="fields"
            items-per-page-select
            :items-per-page="25"
            hover
            sorter
            :sorter-value="{ column: 'date', asc: false }"
            pagination
            responsive
          >
            <template #show_image="{ item }">
              <td class="py-2">
                <CImg thumbnail :src="getImage(item)" height="56" width="56" :alt="`Receipt from ${item.companyName}`" />
              </td>
            </template>
            <template #date="{ item }">
              <td class="py-2">{{ formatDate(item.date) }}</td>
            </template>
            <template #totalAmount="{ item }">
              <td class="py-2 text-right font-weight-bold">{{ money(item.totalAmount) }}</td>
            </template>
            <template #actions="{ item }">
              <td class="py-2 text-right text-nowrap">
                <CButton color="light" size="sm" class="border" :aria-label="`Edit receipt from ${item.companyName}`" title="Edit" @click="onEdit(item)">
                  <CIcon name="cil-pencil" />
                </CButton>
                <CButton color="light" size="sm" class="border text-danger ml-1" :aria-label="`Delete receipt from ${item.companyName}`" title="Delete" @click="showDeleteConfirmation(item)">
                  <CIcon name="cil-trash" />
                </CButton>
              </td>
            </template>
          </CDataTable>
        </CCardBody>

        <!-- Phone / tablet cards -->
        <CCardBody class="d-lg-none p-2">
          <div v-for="item in sortedRows" :key="item.id" class="border rounded p-2 mb-2 d-flex align-items-center">
            <CImg thumbnail :src="getImage(item)" height="56" width="56" class="mr-3 flex-shrink-0" :alt="`Receipt from ${item.companyName}`" />
            <div class="flex-grow-1 min-w-0">
              <router-link :to="`/employee/Receipt/${item.id}`" class="font-weight-bold d-block text-truncate">{{ item.companyName }}</router-link>
              <small class="text-muted">{{ formatDate(item.date) }}</small>
              <div class="font-weight-bold">{{ money(item.totalAmount) }}</div>
            </div>
            <CDropdown color="light" placement="bottom-end">
              <template #toggler-content>
                <CIcon name="cil-options" />
                <span class="sr-only">More actions</span>
              </template>
              <CDropdownItem @click="onEdit(item)">
                <CIcon name="cil-pencil" class="mr-2" /> Edit
              </CDropdownItem>
              <CDropdownDivider />
              <CDropdownItem class="text-danger" @click="showDeleteConfirmation(item)">
                <CIcon name="cil-trash" class="mr-2" /> Delete
              </CDropdownItem>
            </CDropdown>
          </div>
        </CCardBody>
      </template>
    </CCard>

    <CModal
      title="Delete this receipt?"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      <p class="mb-0">
        The receipt from <strong>{{ itemToDelete.companyName }}</strong>
        dated {{ formatDate(itemToDelete.date) }} ({{ money(itemToDelete.totalAmount) }}) will be removed.
        This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" /> Delete receipt
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ReceiptApi from "@/lib/receiptApi";
import { formatDate, asDateString } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";

const fields = [
  { key: "show_image", label: "", sorter: false, filter: false, _style: "width:1%" },
  { key: "date", label: "Date" },
  { key: "companyName", label: "Shop or company" },
  { key: "totalAmount", label: "Amount", _style: "text-align:right" },
  { key: "actions", label: "", _style: "width:1%", sorter: false, filter: false },
];

export default {
  name: "ReceiptList",
  data() {
    return {
      loading: true,
      loadError: false,
      items: [],
      fields,
      api: new ReceiptApi(),
      warningModal: false,
      deleting: false,
      itemToDelete: {},
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", []);
    this.refreshTable();
  },
  computed: {
    rows() {
      return this.items.map((item) => ({
        ...item,
        companyName: item.companyName || "Unknown shop",
        totalAmount: parseFloat(item.totalAmount) || 0,
      }));
    },
    sortedRows() {
      return [...this.rows].sort((a, b) =>
        asDateString(b.date).localeCompare(asDateString(a.date))
      );
    },
  },
  methods: {
    formatDate,
    money(v) {
      return `RM ${formatMyr(v)}`;
    },
    getImage(item) {
      return apiUrl + "documents/file/" + item.documentId;
    },
    refreshTable() {
      this.loading = true;
      this.loadError = false;
      this.api
        .getListByCurrentUser()
        .then((response) => {
          this.items = response.result || [];
        })
        .catch((err) => {
          console.error("Couldn't load receipts", err);
          this.loadError = true;
          if (this.items.length) {
            this.$toast.error("Couldn't refresh your receipts. Check your connection and try again.");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/employee/Receipt/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      this.deleting = true;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success("Receipt deleted");
          this.warningModal = false;
          this.itemToDelete = {};
          this.refreshTable();
        })
        .catch(({ data }) => {
          this.$toast.error(`Couldn't delete the receipt. ${helper.getErrorMessage(data)}`);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    addNew() {
      this.$router.push({ path: "/employee/Receipt" });
    },
  },
};
</script>

<style scoped>
.min-w-0 {
  min-width: 0;
}
</style>
